import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) {
      throw new Error('Details element not found');
    }

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) {
      throw new Error('Summary element not found');
    }

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    // Apply default open state only once
    if (!this.dataset.defaultStateApplied) {
      this.#setDefaultOpenState();
      this.dataset.defaultStateApplied = 'true';
    }

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });

    // If you want the accordion to re-evaluate on breakpoint change,
    // keep this listener. In most cases it's better to preserve user state,
    // so we do not reset open/close state here.
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * Prevent opening/closing when disabled for current breakpoint.
   * @param {Event} event
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
    }
  };

  /**
   * Handles media query change.
   * Do not force reset accordion state on resize.
   */
  #handleMediaQueryChange = () => {
    // Intentionally left blank to preserve current user state
  };

  /**
   * When one accordion opens, close siblings in the same scope.
   */
  #handleToggle = () => {
    if (!this.details.open) return;

    let scope = this.parentElement;

    while (scope && scope !== document.body) {
      const count = scope.querySelectorAll?.('accordion-custom')?.length ?? 0;
      if (count > 1) break;
      scope = scope.parentElement;
    }

    const root = scope || document;

    root.querySelectorAll('accordion-custom').forEach((acc) => {
      if (acc === this) return;
      // Don't close accordions that are forced open by default
      if (acc.dataset.forceOpen === 'true') return;

      const d = acc.querySelector('details');
      if (d instanceof HTMLDetailsElement) {
        d.open = false;
      }
    });
  };

  /**
   * Sets default open state once based on attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    const shouldOpen =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));

    if (shouldOpen) {
      this.details.open = true;
    }
  }

  /**
   * Handles keydown events.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();
      this.details.open = false;
      this.summary.focus();
    }
  };
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}